import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-sm border border-border bg-card p-5" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/55">{label}</p>
    <p className="mt-2 font-display text-4xl font-medium text-primary">{value}</p>
  </div>
);

const Breakdown = ({ title, data, total, suffix = "", testId }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-sm border border-border bg-card p-5" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/55">{title}</p>
      <div className="mt-4 grid gap-3">
        {entries.length === 0 && <p className="text-sm text-foreground/60">No data yet.</p>}
        {entries.map(([k, v]) => {
          const pct = suffix ? v : total ? Math.round((v / total) * 100) : 0;
          return (
            <div key={k}>
              <div className="flex justify-between text-sm">
                <span className="text-primary">{k}</span>
                <span className="text-foreground/70">
                  {suffix ? `${v}${suffix}` : `${v} · ${pct}%`}
                </span>
              </div>
              <div className="mt-1 h-1 bg-border">
                <div className="h-1 bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ResultRow = ({ r, index }) => (
  <tr data-testid={`admin-quiz-row-${index}`} className="border-b border-border last:border-0">
    <td className="p-4 align-top font-semibold text-primary">{r.personality}</td>
    <td className="p-4 align-top">
      <span className="font-display text-2xl text-primary">{r.score}</span>
      <span className="text-xs text-foreground/60">/10 · {r.knowledge_correct}/3 knowledge</span>
    </td>
    <td className="p-4 align-top text-sm text-foreground/80">
      <div>
        <span className="text-foreground/55">Strong:</span> {r.strongest_area}
      </div>
      <div>
        <span className="text-foreground/55">Work on:</span> {r.weakest_area}
      </div>
    </td>
    <td className="p-4 align-top text-sm text-foreground/70">
      {r.email ? (
        <span className="inline-flex items-center gap-1.5">
          <Mail size={13} /> {r.email}
        </span>
      ) : (
        <span className="text-foreground/45">anonymous</span>
      )}
    </td>
    <td className="p-4 align-top text-xs text-foreground/60 whitespace-nowrap">
      <span className="inline-flex items-center gap-1.5">
        <Calendar size={12} /> {new Date(r.created_at).toLocaleString("en-IN")}
      </span>
    </td>
  </tr>
);

export const QuizResultsPanel = ({ token, onUnauthorized, refreshKey }) => {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { "X-Admin-Token": token };
    setLoading(true);
    Promise.all([
      axios.get(`${API}/quiz/stats`, { headers }),
      axios.get(`${API}/quiz/results`, { headers }),
    ])
      .then(([s, r]) => {
        setStats(s.data);
        setRows(r.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) onUnauthorized();
        else toast.error("Couldn't load quiz results.");
      })
      .finally(() => setLoading(false));
  }, [token, refreshKey, onUnauthorized]);

  return (
    <div data-testid="admin-quiz-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Completions" value={stats?.total ?? "—"} testId="quiz-stat-total" />
        <StatCard label="Avg well-being score" value={stats ? `${stats.avg_score}/10` : "—"} testId="quiz-stat-avg" />
        <StatCard label="Avg knowledge" value={stats ? `${stats.avg_knowledge}/3` : "—"} testId="quiz-stat-knowledge" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Breakdown title="Money personalities" data={stats?.personalities} total={stats?.total} testId="quiz-breakdown-personalities" />
        <Breakdown title="Most common area to work on" data={stats?.weakest_areas} total={stats?.total} testId="quiz-breakdown-weakest" />
        <Breakdown title="Average score by area" data={stats?.area_averages} suffix="%" testId="quiz-breakdown-areas" />
      </div>

      <div className="mt-8 overflow-x-auto rounded-sm border border-border bg-card">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {["Personality", "Well-being", "Areas", "Email", "Completed"].map((h) => (
                <th key={h} className="p-4 text-xs font-bold uppercase tracking-wider text-primary">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-12 text-center text-foreground/60">Loading results...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-12 text-center text-foreground/60" data-testid="admin-quiz-empty">
                  No quiz completions yet. Results appear here as soon as someone finishes the passbook.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => <ResultRow key={r._id || r.id || i} r={r} index={i} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
